import { Button } from "components/Button";
import { InputDatePicker } from "components/InputDatePicker";
import { Modal } from "components/Modal";
import { RadioButton } from "components/RadioButton";
import React, { KeyboardEvent, useState } from "react";
import styled from "styled-components";
import { formattingDate } from "utils/formattingInputs";
import {
  Customer,
  PersonSelectPayload,
  usePersonSearch,
} from "./usePersonSearch";

interface PersonSearchModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (payload: PersonSelectPayload) => void;
}

export const PersonSearchModal: React.FC<PersonSearchModalProps> = ({
  open,
  onClose,
  onSelect,
}) => {
  const {
    searchText,
    setSearchText,
    customers,
    isLoading,
    isUnmasking,
    error,
    hasMore,
    hasSearched,
    dateFilter,
    setDateFilter,
    specificDate,
    setSpecificDate,
    handleSearch,
    handleLoadMore,
    fetchUnmasked,
  } = usePersonSearch();

  const [searchError, setSearchError] = useState<string | null>(null);

  const handleSearchClick = () => {
    const kw = searchText.trim();
    if (kw.length > 0 && kw.length < 2) {
      setSearchError("Digite pelo menos 2 caracteres para buscar.");
      return;
    }
    setSearchError(null);
    handleSearch();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSearchClick();
    }
  };

  const handleRowClick = (customer: Customer) => {
    if (isUnmasking) return;
    fetchUnmasked(customer, (payload) => {
      onSelect(payload);
      onClose();
    });
  };

  const handleClose = () => {
    if (isUnmasking) return;
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalContainer>
        <Title>Buscar Pessoa</Title>

        <SearchSection>
          <SearchRow>
            <SearchInput
              type="text"
              placeholder="Buscar por nome, e-mail, telefone ou CPF"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isUnmasking}
            />
            <Button
              label="Buscar"
              typeBtn="blue"
              onClick={handleSearchClick}
              type="button"
              load={isLoading}
              disable={isLoading || isUnmasking}
            />
          </SearchRow>
          {searchError && <InlineError>{searchError}</InlineError>}
        </SearchSection>

        <FilterSection>
          <FilterLabel>Filtrar por cadastro</FilterLabel>
          <RadioGroup>
            <RadioButton
              id="filter-none"
              name="dateFilter"
              label="Sem filtro"
              checked={dateFilter === "none"}
              onChange={() => {
                setDateFilter("none");
                setSpecificDate(null);
              }}
            />
            <RadioButton
              id="filter-24h"
              name="dateFilter"
              label="Últimas 24h"
              checked={dateFilter === "24h"}
              onChange={() => setDateFilter("24h")}
            />
            <RadioButton
              id="filter-week"
              name="dateFilter"
              label="Esta semana"
              checked={dateFilter === "week"}
              onChange={() => setDateFilter("week")}
            />
            <RadioButton
              id="filter-specific"
              name="dateFilter"
              label="Data específica"
              checked={dateFilter === "specific"}
              onChange={() => setDateFilter("specific")}
            />
          </RadioGroup>
          {dateFilter === "specific" && (
            <InputDatePicker
              name="specificDate"
              label="Data"
              selected={specificDate}
              onChange={setSpecificDate}
              block
            />
          )}
        </FilterSection>

        <ResultsSection>
          {isLoading && customers.length === 0 && (
            <StateMessage>Buscando...</StateMessage>
          )}

          {!isLoading && !hasSearched && (
            <StateMessage>
              Busque por nome, e-mail, CPF ou telefone, ou selecione um filtro acima para ver resultados.
            </StateMessage>
          )}

          {!isLoading && hasSearched && customers.length === 0 && !error && (
            <StateMessage>Nenhum resultado encontrado.</StateMessage>
          )}

          {error && <ErrorText>{error}</ErrorText>}

          {customers.length > 0 && (
            <ResultsWrapper>
              {isUnmasking && (
                <UnmaskOverlay>
                  <StateMessage>Carregando dados do cliente...</StateMessage>
                </UnmaskOverlay>
              )}
              <ResultsTable>
                <ResultsHeader>
                  <span>Nome</span>
                  <span>E-mail</span>
                  <span>CPF</span>
                  <span>Telefone</span>
                  <span>Último login</span>
                </ResultsHeader>
                {customers.map((customer) => (
                  <ResultRow
                    key={customer.customerId}
                    onClick={() => handleRowClick(customer)}
                    disabled={isUnmasking}
                  >
                    <span>{customer.name ?? "—"}</span>
                    <span>{customer.email ?? "—"}</span>
                    <span>{customer.cpf ?? "—"}</span>
                    <span>{customer.phoneNumber ?? "—"}</span>
                    <span>
                      {customer.lastTimeLogged
                        ? formattingDate(customer.lastTimeLogged, true)
                        : "—"}
                    </span>
                  </ResultRow>
                ))}
              </ResultsTable>

              {hasMore && (
                <LoadMoreContainer>
                  <Button
                    label="Carregar mais"
                    typeBtn="outline"
                    onClick={handleLoadMore}
                    type="button"
                    load={isLoading}
                    disable={isLoading || isUnmasking}
                  />
                </LoadMoreContainer>
              )}
            </ResultsWrapper>
          )}
        </ResultsSection>

        <FooterActions>
          <Button
            label="Cancelar"
            typeBtn="outline"
            onClick={handleClose}
            type="button"
            disable={isUnmasking}
          />
        </FooterActions>
      </ModalContainer>
    </Modal>
  );
};

const ModalContainer = styled.div`
  background: #fff;
  padding: 1.5rem;
  min-width: 640px;
  max-width: 860px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  overflow-y: auto;
`;

const Title = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #3a3956;
  margin: 0;
`;

const SearchSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const SearchRow = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 0.625rem 0.75rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 0.9rem;
  color: #333;

  &:focus {
    outline: none;
    border-color: #312cc1;
  }

  &:disabled {
    background: #f5f5f5;
    cursor: not-allowed;
  }
`;

const InlineError = styled.span`
  font-size: 0.8rem;
  color: #d32f2f;
`;

const ErrorText = styled.p`
  font-size: 0.875rem;
  color: #d32f2f;
  margin: 0;
`;

const FilterSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FilterLabel = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  color: #3a3956;
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
`;

const ResultsSection = styled.div`
  flex: 1;
  min-height: 60px;
  position: relative;
`;

const StateMessage = styled.p`
  font-size: 0.875rem;
  color: #666;
  margin: 0.5rem 0;
  text-align: center;
`;

const ResultsWrapper = styled.div`
  position: relative;
`;

const UnmaskOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
`;

const ResultsTable = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
`;

const ResultsHeader = styled.div`
  display: grid;
  grid-template-columns: 2fr 2fr 1.5fr 1.5fr 1.5fr;
  padding: 0.5rem 0.75rem;
  background: #f5f5f5;
  font-size: 0.8rem;
  font-weight: 600;
  color: #3a3956;
  gap: 0.5rem;
`;

const ResultRow = styled.div<{ disabled?: boolean }>`
  display: grid;
  grid-template-columns: 2fr 2fr 1.5fr 1.5fr 1.5fr;
  padding: 0.625rem 0.75rem;
  font-size: 0.85rem;
  color: #333;
  gap: 0.5rem;
  border-top: 1px solid #e0e0e0;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};

  &:hover {
    background: ${({ disabled }) => (disabled ? "transparent" : "#f0f4ff")};
  }

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const LoadMoreContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 0.75rem 0;
`;

const FooterActions = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: 0.75rem;
  border-top: 1px solid #e0e0e0;
`;
